import { useRef, useState } from "react";
import api from "@/lib/api";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Upload, Loader2, X, Video as VideoIcon } from "lucide-react";

const MAX_BYTES = 50 * 1024 * 1024;

export const MediaUploader = ({ images = [], videos = [], onImages, onVideos }) => {
  const [progress, setProgress] = useState(null);
  const [busyKind, setBusyKind] = useState("");
  const imgRef = useRef(null);
  const vidRef = useRef(null);

  const upload = async (file, expect) => {
    if (!file) return;
    if (file.size > MAX_BYTES) { toast.error("File too large — 50MB maximum."); return; }
    const fd = new FormData();
    fd.append("file", file);
    setBusyKind(expect);
    setProgress(0);
    try {
      const { data } = await api.post("/seller/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (p) => p.total && setProgress(Math.round((p.loaded / p.total) * 100)),
      });
      if (data.kind === "video") onVideos([...(videos || []), data.url]);
      else onImages([...(images || []), data.url]);
      toast.success(`${data.kind === "video" ? "Video" : "Image"} uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setProgress(null); setBusyKind("");
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {(images || []).map((url) => (
          <div key={url} className="relative w-16 h-16 rounded-lg overflow-hidden border group">
            <img src={url} alt="" className="w-full h-full object-cover" />
            <button type="button" onClick={() => onImages(images.filter((u) => u !== url))} className="absolute top-0 right-0 bg-black/60 p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <X className="w-3 h-3 text-white" />
            </button>
          </div>
        ))}
        {(videos || []).map((url) => (
          <div key={url} className="relative w-16 h-16 rounded-lg overflow-hidden border group bg-black">
            <video src={url} className="w-full h-full object-cover" muted />
            <span className="absolute inset-0 flex items-center justify-center pointer-events-none"><VideoIcon className="w-5 h-5 text-white/90" /></span>
            <button type="button" onClick={() => onVideos(videos.filter((u) => u !== url))} className="absolute top-0 right-0 bg-black/60 p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <X className="w-3 h-3 text-white" />
            </button>
          </div>
        ))}
      </div>

      {progress !== null && (
        <div data-testid="upload-progress" className="space-y-1">
          <Progress value={progress} className="h-2" />
          <p className="text-xs text-muted-foreground">Uploading {busyKind}… {progress}%</p>
        </div>
      )}

      <div className="flex gap-2">
        <label className="flex-1 flex items-center justify-center gap-2 p-2.5 border border-dashed rounded-lg cursor-pointer hover:border-primary text-sm">
          {busyKind === "image" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}Add images
          <input ref={imgRef} data-testid="prod-image-upload" type="file" accept="image/*" hidden disabled={progress !== null} onChange={(e) => upload(e.target.files?.[0], "image")} />
        </label>
        <label className="flex-1 flex items-center justify-center gap-2 p-2.5 border border-dashed rounded-lg cursor-pointer hover:border-primary text-sm">
          {busyKind === "video" ? <Loader2 className="w-4 h-4 animate-spin" /> : <VideoIcon className="w-4 h-4" />}Add video
          <input ref={vidRef} data-testid="prod-video-upload" type="file" accept="video/*" hidden disabled={progress !== null} onChange={(e) => upload(e.target.files?.[0], "video")} />
        </label>
      </div>
      <p className="text-[11px] text-muted-foreground">JPG/PNG/WEBP/GIF or MP4/WEBM · max 50MB · thumbnails generated automatically.</p>
    </div>
  );
};
